import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useAuditorExceptionReport } from '../../../hooks/analytics/useAuditorReports.ts';
import { formatPercent, RefreshMeta } from '../controller/shared.tsx';
import type { AuditorExceptionReportRow } from '../../../request/analytics/auditorReports.ts';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  moderate: 'bg-amber-100 text-amber-700',
  low: 'bg-yellow-100 text-yellow-700',
};

/** Severity-colored chip; unrecognized/unset severities render neutral gray. */
const SeverityChip: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  if (!value) {
    return <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">Unrated</span>;
  }
  const key = value.toLowerCase();
  const classes = SEVERITY_COLORS[key] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${classes}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
};

/**
 * Report #39 — Exception Report. Read-only, sample-level exceptions across
 * the org's current test runs, severity-colored per the docx UI column.
 */
export const ExceptionReportTable: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useAuditorExceptionReport();
  const rows = useMemo(() => {
    const base = data ?? [];
    return controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
  }, [data, controlIdFilter]);

  // Right-edge clipping fix (feedback round 2026-08-23): these 7 widthPercent
  // values summed to exactly 100, so AppDataTable's default mode scales
  // (never overflows) the table to the container width — narrow cards then
  // squeeze the "Deviation Rate" header down to where it clips mid-word
  // ("DEVIATIO RATE"). Bumping the total above 100% opts into the same
  // `allowColumnOverflowScroll` overflow-and-scroll pattern already used by
  // Controls.js/Risks.js: the table grows past the card width and the
  // existing `overflow-x-auto` scroll wrapper (Table.tsx) takes over instead
  // of clipping.
  const columns = useMemo<TableColumnDef<AuditorExceptionReportRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_id}</span>,
        widthPercent: 12,
      },
      {
        id: 'title',
        columnLabel: 'Title',
        header: () => 'Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 22,
      },
      {
        id: 'attribute',
        columnLabel: 'Attribute',
        header: () => 'Attribute',
        cell: ({ row }) => row.original.label ?? row.original.attribute_ref ?? '—',
        widthPercent: 16,
      },
      {
        id: 'record_ref',
        columnLabel: 'Sample',
        header: () => 'Sample',
        cell: ({ row }) => row.original.record_ref,
        widthPercent: 14,
      },
      {
        id: 'observation',
        columnLabel: 'Observation',
        header: () => 'Observation',
        cell: ({ row }) => row.original.observation ?? '—',
        widthPercent: 26,
        textOverflowBehavior: 'truncate',
      },
      {
        id: 'severity',
        columnLabel: 'Severity',
        header: () => 'Severity',
        cell: ({ row }) => <SeverityChip value={row.original.severity} />,
        widthPercent: 14,
      },
      {
        id: 'deviation_rate',
        columnLabel: 'Deviation Rate',
        header: () => 'Deviation Rate',
        cell: ({ row }) => formatPercent(row.original.deviation_rate != null ? row.original.deviation_rate * 100 : null),
        widthPercent: 14,
      },
    ],
    []
  );

  return (
    <AppDataTable<AuditorExceptionReportRow>
      data={rows}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load the exception report.' : null}
      emptyMessage="No exceptions found on current test runs."
      searchable
      searchPlaceholder="Search exceptions..."
      paginated
      defaultPageSize={10}
      showPageSizeSelector
      allowColumnOverflowScroll
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default ExceptionReportTable;
